import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Column,
  Copy,
  Flex,
  Row,
  Square,
  Stack,
  SvgMagnifyingGlass,
} from '@grubhubprod/gh-cookbook-react';
import { useForm } from 'react-hook-form/dist/index.ie11';
import { ENTERPRISE_SETUP_TABLE_DEFAULT_HEIGHT } from 'src/components/EnterpriseSetupTable/EnterpriseSetupTable';
import { Input } from 'src/components/Input/Input';
import { NoResultsMsg } from 'src/components/Table/NoResultsMsg';
import Table from 'src/components/Table/Table';
import { pushError } from 'src/utilities/notifications';
import { EnterpriseInvoiceRecipientModal } from 'src/components/enterprise/set-up/steps/EnterpriseInvoiceSettingsStep/EnterpriseInvoiceRecipientModal';
import {
  getInvoiceContacts,
  IInvoiceContact,
  SubtotalInvoiceMethod,
} from 'src/utilities/invoiceContact.utils';
import { EnterpriseInvoiceRecipientTableRow } from './EnterpriseInvoiceRecipientTableRow';

interface SearchFormFields {
  searchQuery: string;
}

interface IEnterpriseInvoiceRecipientTableProps {
  corpId: string;
}

export function EnterpriseInvoiceRecipientTable({
  corpId,
}: IEnterpriseInvoiceRecipientTableProps): JSX.Element {
  const { register: searchFormRegister, handleSubmit: handleSearchFormSubmit } =
    useForm<SearchFormFields>();
  const [invoiceContactSearchResults, setInvoiceContactSearchResults] =
    useState<{
      totalContacts: number;
      invoiceContactList: IInvoiceContact[];
      filteredInvoiceContactList: IInvoiceContact[];
    }>({
      totalContacts: 0,
      invoiceContactList: [],
      filteredInvoiceContactList: [],
    });
  const [isTableLoading, setIsTableLoading] = useState(false);
  const [showAddInvoiceContactModal, setShowAddInvoiceContactModal] =
    useState(false);
  const currentSearchQuery = useRef('');

  const { totalContacts, invoiceContactList, filteredInvoiceContactList } =
    invoiceContactSearchResults;

  useEffect(() => {
    if (corpId) {
      getInvoiceContactResults();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [corpId]);

  async function getInvoiceContactResults() {
    try {
      setIsTableLoading(true);
      const invoiceContacts: IInvoiceContact[] = await getInvoiceContacts({
        corpId,
        subtotalMethod: SubtotalInvoiceMethod.MEAL_TYPE,
      });

      setInvoiceContactSearchResults({
        invoiceContactList: invoiceContacts,
        filteredInvoiceContactList: invoiceContacts,
        totalContacts: invoiceContacts.length,
      });
    } catch (error) {
      pushError('Unable to retrieve invoice recipients. Please try again.');
    } finally {
      setIsTableLoading(false);
    }
  }

  function filterInvoiceContacts({ searchQuery }: SearchFormFields) {
    setIsTableLoading(true);
    currentSearchQuery.current = searchQuery;
    const filteredList = invoiceContactList.filter((invoiceContact) => {
      const regExp = new RegExp(searchQuery, 'i');
      const matchesFirstName = invoiceContact.first_name.match(regExp);
      const matchesLastName = invoiceContact.last_name.match(regExp);
      const matchesEmail = invoiceContact.email.match(regExp);

      return matchesFirstName || matchesLastName || matchesEmail;
    });

    setInvoiceContactSearchResults({
      ...invoiceContactSearchResults,
      filteredInvoiceContactList: filteredList,
    });
    setIsTableLoading(false);
  }

  return (
    <>
      <Row gutter={0} as="form">
        <Column tablet={5} gutter={0}>
          <Stack x="xx-sm">
            <Input
              name="searchQuery"
              id="searchQuery"
              ariaLabel="Search by name or email"
              placeholder="Search by name or email"
              prefixIcon={
                <Box color="secondaryText">
                  <SvgMagnifyingGlass size="20px" />
                </Box>
              }
              ref={searchFormRegister}
            />
          </Stack>
        </Column>
        <Column tablet={2} gutter={0}>
          <Button
            variant="secondary"
            buttonSize="medium"
            block
            disabled={totalContacts === 0}
            onClick={handleSearchFormSubmit(filterInvoiceContacts)}
          >
            Search
          </Button>
        </Column>
      </Row>
      <Stack y="x-sm">
        <Flex justifyContent="flex-end">
          <Copy variant="body" color="secondaryText">
            {filteredInvoiceContactList.length} of {totalContacts}
          </Copy>
        </Flex>
      </Stack>
      <Table
        isSignupOrSetupTable
        headerInsets="sm"
        isLoading={isTableLoading}
        header={() => (
          <Row gutter={8}>
            <Column phone={4} gutter={8}>
              <Copy variant="body" color="secondaryText">
                Full name
              </Copy>
            </Column>
            <Column phone={3} gutter={8}>
              <Copy variant="body" color="secondaryText">
                Email
              </Copy>
            </Column>
            <Column phone={3} gutter={8}>
              <Copy variant="body" color="secondaryText">
                Phone number
              </Copy>
            </Column>
          </Row>
        )}
        bodyMaxHeight={ENTERPRISE_SETUP_TABLE_DEFAULT_HEIGHT}
        body={() => (
          <>
            {filteredInvoiceContactList.length > 0 && (
              <ul className="removeListStyles">
                {filteredInvoiceContactList.map((invoiceContact, index) => (
                  <EnterpriseInvoiceRecipientTableRow
                    key={invoiceContact.invoice_contact_id}
                    corpId={corpId}
                    invoiceContact={invoiceContact}
                    isLastRow={index === filteredInvoiceContactList.length - 1}
                    onRemovalSuccess={() => getInvoiceContactResults()}
                    onEditSuccess={() => getInvoiceContactResults()}
                  />
                ))}
              </ul>
            )}
            {totalContacts === 0 && (
              <NoResultsMsg message="You haven't added any invoice recipients yet" />
            )}
            {filteredInvoiceContactList.length === 0 && totalContacts > 0 && (
              <NoResultsMsg
                message={`No results matching '${currentSearchQuery.current}'`}
              />
            )}
          </>
        )}
        showFooter
        footerMinHeight="auto"
        justifyFooter="flex-start"
        footer={() => (
          <Square inset="sm" width="100%">
            <Button
              variant="secondary"
              onClick={() => setShowAddInvoiceContactModal(true)}
            >
              Add recipient
            </Button>
          </Square>
        )}
      />
      {showAddInvoiceContactModal && (
        <EnterpriseInvoiceRecipientModal
          corpId={corpId}
          onClose={() => setShowAddInvoiceContactModal(false)}
          onSuccess={() => getInvoiceContactResults()}
        />
      )}
    </>
  );
}
